import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { ArrowUpIcon, ArrowDownIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { Card, Loading } from '../common';

const LiquidityWidget = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchForecast();
    }, []);

    const fetchForecast = async () => {
        try {
            const response = await api.get('/forecast');
            if (response.data.success) {
                setData(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch liquidity forecast:', err);
            setError(t('errors.generic') || 'Could not load liquidity forecast.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'low_liquidity': return 'text-red-600 bg-red-50 border-red-200';
            case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'safe': return 'text-green-600 bg-green-50 border-green-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getBadgeColor = (status) => {
        switch (status) {
            case 'low_liquidity': return 'bg-red-100 text-red-800';
            case 'warning': return 'bg-yellow-100 text-yellow-800';
            case 'safe': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'low_liquidity': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
            case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
            case 'safe': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            default: return null;
        }
    };

    if (loading) return <div className="p-4"><Loading size="sm" /></div>;
    if (error) return <div className="text-red-500 text-sm p-4">{error}</div>;
    if (!data || data.length === 0) return null;

    // Filter only important currencies or those with warnings
    const importantCurrencies = data.filter(c =>
        ['USD', 'IQD'].includes(c.currencyCode) || c.status !== 'safe' || c.currentBalance > 0
    );

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('liquidity.title')}
                </h3>
                <button
                    onClick={() => navigate('/liquidity')}
                    className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center font-medium"
                >
                    {t('liquidity.viewDetails')}
                    <ArrowRightIcon className="h-4 w-4 ml-1 rtl:ml-0 rtl:mr-1" />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {importantCurrencies.map((item) => (
                    <div key={item.currencyCode} className={`p-4 rounded-lg border ${getStatusColor(item.status)}`}>
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center">
                                {getStatusIcon(item.status)}
                                <span className="ml-2 font-bold text-gray-900">{item.currencyCode}</span>
                            </div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getBadgeColor(item.status)}`}>
                                {t(`liquidity.${item.status}`)}
                            </span>
                        </div>

                        <div className="text-sm text-gray-600 mb-2">
                            {t('liquidity.currentBalance')}: <strong className="text-gray-900">{item.currentBalance.toLocaleString()}</strong>
                        </div>

                        {item.status !== 'safe' && (
                            <div className="bg-white/50 border border-black/5 p-2 rounded text-xs text-gray-800 font-medium mb-1">
                                <strong>{t('liquidity.actionRequired')}:</strong> {item.recommendation}
                            </div>
                        )}

                        {item.status === 'safe' && (
                            <div className="text-xs text-green-700 flex items-center">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                {t('liquidity.sufficientReserves')} ({item.avgDailyOutflow > 0 ? Math.floor(item.currentBalance / item.avgDailyOutflow) : '30+'} {t('common.days') || 'days'})
                            </div>
                        )}

                        <div className="mt-2 text-xs text-gray-500 border-t border-black/5 pt-2">
                            {t('liquidity.avgDailyOutflow')}: {Math.round(item.avgDailyOutflow).toLocaleString()}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

export default LiquidityWidget;
